import React from "react";
import { Form, FormProps } from "@/components/form";
import { getAllClientes } from "@/features/auxiliar/clientes/api/get-all-clientes";
import { getCliente } from "@/features/auxiliar/clientes/api/get-cliente";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { Chip, Divider, Stack, StackProps, Switch, Typography } from "@mui/material";
import { z } from "zod/v4";
import { useOperationalMode } from "../hooks/use-operational-mode";
import usePrintBold from "../hooks/use-print-bold";

export const ventaConfigurationFormSchema = z.object({
    paySeparately: z.boolean().default(true),
    requireVentaAuthorization: z.boolean().default(true),
    ventaAuthorizationPassword: z.string().default(""),
    defaultPrecioIndex: z.number().default(0),
    articuloNombreMaxLength: z.number().default(0),
    marginTopPx: z.number().default(150),
    itemsPerPage: z.number().default(30),
    horizontal: z.boolean().default(true),
    showMonedaId: z.string().default(""),
    genericClientId: z.string().default(""),
    modeTicket: z.boolean().default(false),
    printFont: z.string().default(""),
    printFontSize: z.number().default(0),
    printPaddingRight: z.number().default(8),
    printPaperWidth: z.string().default("80mm"),
    ticketHeader: z.string().default(""),
    ticketItemIdentifier: z.string().default("cod"),
    nuevaVentaItemIdentifier: z.string().default("cod"),
    ticketFooter: z.string().default(""),
    kudeItemsPerPage: z.number().default(7)
})

export default function VentaConfigurationForm({
    form,
    ...props
}: FormProps<z.input<typeof ventaConfigurationFormSchema>>) {
    const { monedas } = useMonetizacion();
    const { facturacionEnabled } = useOperationalMode();
    const [printBold, setPrintBold] = usePrintBold();

    return <Form form={form} gap={3} {...props}>
        <Stack gap={3}>
            <Stack gap={2}>
                <Stack gap={1}>
                    <Subtitle>Proceso de Venta</Subtitle>
                    <Typography>Determina si la creación y el pago de la venta se realiza junto o separado.</Typography>
                </Stack>
                <Stack maxWidth={"350px"}>
                    <Form.Select name="paySeparately" size="small">
                        <Form.Select.Option value={false}>Junto</Form.Select.Option>
                        <Form.Select.Option value={true}>Separado</Form.Select.Option>
                    </Form.Select>
                </Stack>
            </Stack>

            <Stack gap={2}>
                <Stack gap={1}>
                    <Subtitle>Código de Autorización de Precios</Subtitle>
                    <Typography>Determina si se requiere un código de autorización para usar Precio 3 y precio personalizado en una venta.</Typography>
                </Stack>
                <Stack maxWidth={"350px"}>
                    <Form.Select name="requireVentaAuthorization" size="small">
                        <Form.Select.Option value={true}>Requerido</Form.Select.Option>
                        <Form.Select.Option value={false}>No requerido</Form.Select.Option>
                    </Form.Select>
                </Stack>
                <Stack maxWidth={"350px"}>
                    <Form.PasswordField name="ventaAuthorizationPassword" size="small" label="Contraseña de autorización" />
                </Stack>
            </Stack>

            <Stack gap={2}>
                <Stack gap={1}>
                    <Subtitle>Precio Predeterminado</Subtitle>
                    <Typography>El precio que se selecciona por defecto al agregar un artículo.</Typography>
                </Stack>
                <Stack maxWidth={"350px"}>
                    <Form.Select name="defaultPrecioIndex" size="small">
                        <Form.Select.Option value={0}>Precio 1</Form.Select.Option>
                        <Form.Select.Option value={1}>Precio 2</Form.Select.Option>
                        <Form.Select.Option value={2}>Precio 3</Form.Select.Option>
                    </Form.Select>
                </Stack>
            </Stack>

            <Stack gap={2}>
                <Stack gap={1}>
                    <Subtitle>Cliente Predeterminado</Subtitle>
                    <Typography>El cliente que se utiliza por defecto.</Typography>
                </Stack>
                <Stack maxWidth={"350px"}>
                    <Form.ModelComboBox name="genericClientId" get={getCliente} getAll={getAllClientes} size="small" />
                </Stack>
            </Stack>

            <Stack gap={3}>
                <Subtitle>Identificador de Ítem</Subtitle>

                <Stack gap={2}>
                    <Typography>Define qué dato del artículo se imprime en el ticket.</Typography>
                    <Stack maxWidth={"350px"}>
                        <Form.Select name="ticketItemIdentifier" size="small" label="Impresión de Ticket">
                            <Form.Select.Option value="cod">COD (Código de artículo)</Form.Select.Option>
                            <Form.Select.Option value="numItem">NÚM. ÍTEM (Número de ítem de artículo)</Form.Select.Option>
                        </Form.Select>
                    </Stack>
                </Stack>

                <Stack gap={2}>
                    <Typography>Define qué dato del artículo se muestra en la tabla de artículos de Nueva Venta.</Typography>
                    <Stack maxWidth={"350px"}>
                        <Form.Select name="nuevaVentaItemIdentifier" size="small" label="Tabla de Nueva Venta">
                            <Form.Select.Option value="cod">COD (Código de artículo)</Form.Select.Option>
                            <Form.Select.Option value="numItem">NÚM. ÍTEM (Número de ítem de artículo)</Form.Select.Option>
                        </Form.Select>
                    </Stack>
                </Stack>
            </Stack>

            <Stack gap={3}>
                <Subtitle>Diseño de Impresión</Subtitle>

                {/* General: Factura + Ticket */}
                <Subsection tags={["Factura", "Ticket"]}>
                    <Stack maxWidth={"350px"}>
                        <Form.NumberField name="articuloNombreMaxLength" size="small" label="Máx. caracteres nombre artículo" decimalScale={0} slotProps={{ input: { endAdornment: <Typography marginLeft={1}>car.</Typography> } }} helperText="0 = sin límite" />
                    </Stack>
                    <Stack maxWidth={"350px"}>
                        <Form.Select name="showMonedaId" size="small" label="Moneda">
                            {monedas.map(m => <Form.Select.Option key={m.id} value={m.id}>{m.nombre} ({m.simbolo})</Form.Select.Option>)}
                        </Form.Select>
                    </Stack>
                </Subsection>

                {/* Solo Factura */}
                <Subsection tags={["Factura"]}>
                    <Stack maxWidth={"350px"}>
                        <Form.Select name="horizontal" size="small" label="Orientación">
                            <Form.Select.Option value={true}>Horizontal</Form.Select.Option>
                            <Form.Select.Option value={false}>Vertical</Form.Select.Option>
                        </Form.Select>
                    </Stack>
                    <Stack maxWidth={"350px"}>
                        <Form.NumberField name="marginTopPx" size="small" label="Margen Superior" slotProps={{ input: { endAdornment: <Typography marginLeft={1}>Píxeles</Typography> } }} />
                    </Stack>
                    <Stack maxWidth={"350px"}>
                        <Form.NumberField name="itemsPerPage" size="small" label="Artículos por Página" decimalScale={0} slotProps={{ input: { endAdornment: <Typography marginLeft={1}>Artículos</Typography> } }} />
                    </Stack>
                </Subsection>

                {/* Factura + Ticket + KuDE Factura + KuDE Ticket */}
                <Subsection tags={["Factura", "Ticket", ...facturacionEnabled ? ["KuDE Factura", "KuDE Ticket"] : []]}>
                    <Stack maxWidth={"350px"}>
                        <Form.TextField name="printFont" size="small" label="Fuente de Impresión" placeholder="ej: Arial, Courier New" slotProps={{ input: { inputProps: { style: { textAlign: "right" } } } }} />
                    </Stack>
                    <Stack maxWidth={"350px"}>
                        <Form.NumberField name="printFontSize" size="small" label="Tamaño de Fuente" decimalScale={1} slotProps={{ input: { endAdornment: <Typography marginLeft={1}>pt</Typography> } }} />
                    </Stack>
                    <Stack maxWidth={"350px"}>
                        <Form.NumberField name="printPaddingRight" size="small" label="Margen Derecho" decimalScale={1} slotProps={{ input: { endAdornment: <Typography marginLeft={1}>mm</Typography> } }} />
                    </Stack>
                    <Stack maxWidth={"350px"} direction="row" alignItems="center" justifyContent="space-between">
                        <Typography>Imprimir en negrita</Typography>
                        <Switch checked={printBold} onChange={e => setPrintBold(e.target.checked)} />
                    </Stack>
                </Subsection>

                {/* Ticket + KuDE Ticket */}
                <Subsection tags={["Ticket", ...facturacionEnabled ? ["KuDE Ticket"] : []]}>
                    <Stack maxWidth={"350px"}>
                        <Form.TextField name="printPaperWidth" size="small" label="Ancho de Papel" placeholder="ej: 80mm, 58mm" slotProps={{ input: { inputProps: { style: { textAlign: "right" } } } }} />
                    </Stack>
                </Subsection>

                {/* Solo Ticket */}
                <Subsection tags={["Ticket"]}>
                    <Stack maxWidth={"350px"} gap={2}>
                        <Form.TextField name="ticketHeader" size="small" label="Mensaje de Encabezado del Ticket" multiline minRows={2} placeholder="ej: Bienvenido a nuestra tienda." registerOptions={{ enterBehaviour: "none" }} InputLabelProps={{ shrink: true }} />
                        <Form.TextField name="ticketFooter" size="small" label="Mensaje de Pie del Ticket" multiline minRows={2} placeholder="ej: Verifique su mercadería antes de retirarse." registerOptions={{ enterBehaviour: "none" }} InputLabelProps={{ shrink: true }} />
                    </Stack>
                </Subsection>
            </Stack>

            {facturacionEnabled &&
                <Stack gap={2}>
                    <Stack gap={1}>
                        <Subtitle>Factura Electrónica</Subtitle>
                        <Typography>Modo de impresión del KuDE.</Typography>
                    </Stack>
                    <Subsection tags={["KuDE Factura", "KuDE Ticket"]}>
                        <Stack maxWidth={"350px"}>
                            <Form.Select name="modeTicket" size="small" label="Modo de impresión">
                                <Form.Select.Option value={false}>Factura</Form.Select.Option>
                                <Form.Select.Option value={true}>Ticket</Form.Select.Option>
                            </Form.Select>
                        </Stack>
                    </Subsection>
                    <Subsection tags={["KuDE Factura"]}>
                        <Stack maxWidth={"350px"}>
                            <Form.NumberField name="kudeItemsPerPage" size="small" label="Artículos por Página" decimalScale={0} slotProps={{ input: { endAdornment: <Typography marginLeft={1}>Artículos</Typography> } }} />
                        </Stack>
                    </Subsection>
                </Stack>
            }
        </Stack>

        <Stack direction={"row"} alignItems={"center"} justifyContent={"end"}>
            <Form.SubmitButton>Guardar</Form.SubmitButton>
        </Stack>
    </Form>
}

function Subtitle({
    children,
    ...props
}: StackProps) {
    return <Stack gap={1} {...props}>
        <Typography variant="h6">{children}</Typography>
        <Divider flexItem />
    </Stack>
}

function Subsection({
    tags,
    children,
}: {
    tags: string[],
    children: React.ReactNode,
}) {
    const colorMap: Record<string, "primary" | "secondary" | "success" | "warning"> = {
        "Factura": "primary",
        "Ticket": "secondary",
        "KuDE Factura": "success",
        "KuDE Ticket": "warning",
    }
    return <Stack gap={2} paddingLeft={2} borderLeft="3px solid" borderColor="divider">
        <Stack direction="row" gap={1} flexWrap="wrap">
            {tags.map(tag => (
                <Chip key={tag} label={tag} size="small" color={colorMap[tag] ?? "default"} variant="outlined" />
            ))}
        </Stack>
        {children}
    </Stack>
}